/**
 * 审批看板（合同 / 费用 / 发票共用）
 *
 * 【为什么是一个组件而不是"合同审批页 + 报销审批页"】
 * 审批的机制完全一样：都是 `flow_task` + `POST /flow/tasks/:id/handle`，
 * 差别只有业务类型与跳转目标。而现实中**同一个人常常既审合同又审报销**
 * （财务初审、领导、租户管理员），分两个页面意味着他要来两趟。
 * 抄两份的代价与 `AlertBoard` 那边一样：**授权与动作规则会分叉**。
 *
 * 【角色闸与数据范围闸】后端对合同/报销做了两道（发票侧保持原行为）：
 *   ① 调用者角色必须命中任务的处理角色（管理员例外）
 *   ② 业务对象必须落在调用者的数据范围内——部门负责人只审得到本部门的单子
 * 所以这个页面**不自己判断能不能审**，只把按钮画出来，让后端说了算。
 * 前端判错一次就会变成"按钮在但点了 403"或"能审却没按钮"。
 */

import { useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Select, Space, Tag, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { BizType, BIZ_TYPE_LABELS, FlowAction, PermissionCode, RiskLevel } from '@ilc/shared';
import type { FlowTask } from '@ilc/shared';
import { flowApi } from '@/api/endpoints';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { RiskTag } from '@/components/RiskTag';
import { Money } from '@/components/Money';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';

interface Query {
  page?: number;
  pageSize?: number;
  status?: string;
  bizType?: BizType;
  mine?: boolean;
}

/** 任务指向哪个页面的详情 */
function linkFor(task: FlowTask): string {
  switch (task.bizType) {
    case BizType.CONTRACT:
    case BizType.CONTRACT_CHANGE:
      return `/business/contracts/${task.bizId}`;
    case BizType.EXPENSE_REIMBURSE:
      return `/business/expenses/reimburse/${task.bizId}`;
    case BizType.EXPENSE_APPLY:
      return '/business/expenses/apply';
    default:
      return `/business/invoices/${task.bizId}`;
  }
}

export interface ApprovalBoardProps {
  title: string;
  description: React.ReactNode;
  /**
   * 锁定业务类型：给「报销审核」这类专用工作台用。
   * 不传则是跨业务类型的待办中心。
   */
  lockedBizType?: BizType;
}

export function ApprovalBoard(props: ApprovalBoardProps): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  /**
   * 入口权限取**并集**：任一业务类型的读权限都能进这个页面。
   * 真正能看哪些类型由后端的可见集合决定（它按权限逐个判定，
   * 而不是靠中间件的 OR 语义——那只会放宽）。
   */
  const canEnter = can(PermissionCode.INVOICE_READ) || can(PermissionCode.CONTRACT_READ) || can(PermissionCode.EXPENSE_READ);

  const list = usePagedList<FlowTask, Query>({
    loader: (q) =>
      flowApi.tasks({
        ...q,
        ...(props.lockedBizType ? { bizType: props.lockedBizType } : {}),
      } as never),
    // 默认只看待我处理的——待办中心堆满别人的任务就没有意义了
    initialQuery: {
      page: 1,
      pageSize: 20,
      status: 'PENDING',
      mine: true,
      ...(props.lockedBizType ? { bizType: props.lockedBizType } : {}),
    },
  });

  const [approveTarget, setApproveTarget] = useState<FlowTask | null>(null);
  const [rejectTarget, setRejectTarget] = useState<FlowTask | null>(null);
  const [rejectToPrev, setRejectToPrev] = useState<FlowTask | null>(null);
  const [terminateTarget, setTerminateTarget] = useState<FlowTask | null>(null);

  const columns = useMemo(
    () => [
      {
        title: '业务',
        dataIndex: 'bizType',
        width: 96,
        render: (v: BizType) => {
          const color =
            v === BizType.CONTRACT || v === BizType.CONTRACT_CHANGE
              ? 'blue'
              : v === BizType.INVOICE
                ? 'default'
                : 'purple';
          return <Tag color={color}>{BIZ_TYPE_LABELS[v] ?? v}</Tag>;
        },
      },
      {
        title: '单据',
        key: 'subject',
        width: 300,
        render: (_: unknown, r: FlowTask) => (
          <span>
            <Link to={linkFor(r)}>{r.bizSummary?.title ?? r.bizId}</Link>
            {r.bizSummary?.subtitle && (
              <span style={{ color: 'rgba(0,0,0,0.45)', marginLeft: 8 }}>{r.bizSummary.subtitle}</span>
            )}
          </span>
        ),
      },
      {
        title: '金额',
        key: 'amount',
        width: 130,
        align: 'right' as const,
        render: (_: unknown, r: FlowTask) =>
          r.bizSummary?.amountCents === null || r.bizSummary?.amountCents === undefined ? (
            <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
          ) : (
            <Money cents={r.bizSummary.amountCents} />
          ),
      },
      {
        title: '风险',
        key: 'risk',
        width: 80,
        render: (_: unknown, r: FlowTask) =>
          r.bizSummary?.riskLevel && r.bizSummary.riskLevel !== RiskLevel.NONE ? (
            <RiskTag level={r.bizSummary.riskLevel} />
          ) : (
            <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
          ),
      },
      {
        title: '当前节点',
        dataIndex: 'nodeName',
        width: 140,
        render: (v: string, r: FlowTask) => (
          <span>
            {v}
            {/* 特例放行通道要标出来：那是一类特殊的审批，放行理由必填 */}
            {r.nodeKey?.includes('release') && <Tag color="warning">特批</Tag>}
          </span>
        ),
      },
      {
        title: '处理角色',
        dataIndex: 'assigneeRoleName',
        width: 120,
        render: (v: string | null) => v ?? '-',
      },
      {
        title: '超时',
        dataIndex: 'dueAt',
        width: 140,
        render: (v: string | null, r: FlowTask) =>
          !v ? (
            <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
          ) : (
            <Tooltip title={`应处理时间 ${dayjs(v).format('YYYY-MM-DD HH:mm')}`}>
              <span style={{ color: r.overdue ? '#cf1322' : undefined }}>
                {r.overdue ? '已超时 ' : ''}
                {dayjs(v).format('MM-DD HH:mm')}
              </span>
            </Tooltip>
          ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 220,
        fixed: 'right' as const,
        render: (_: unknown, r: FlowTask) => (
          <Space size={4}>
            <Button size="small" type="link" onClick={() => setApproveTarget(r)}>
              通过
            </Button>
            <Button size="small" type="link" danger onClick={() => setRejectTarget(r)}>
              退回经办人
            </Button>
            <Button size="small" type="link" onClick={() => setRejectToPrev(r)}>
              退回上一节点
            </Button>
            <Button size="small" type="link" danger onClick={() => setTerminateTarget(r)}>
              终止
            </Button>
          </Space>
        ),
      },
    ],
    [],
  );

  const doApprove = async (task: FlowTask, comment: string): Promise<void> => {
    /**
     * 特例放行必须填专项依据（后端强制）。这里按节点 key 判断，
     * 与后端的 `allow_special_release` 是同一件事的两个入口——
     * 后端才是规则，前端只是不让人白跑一趟。
     */
    const isRelease = task.nodeKey?.includes('release') ?? false;
    const result = await performAction({
      action: () =>
        flowApi.handle(task.id, {
          action: isRelease ? FlowAction.SPECIAL_RELEASE : FlowAction.APPROVE,
          comment,
          ...(isRelease ? { specialReleaseReason: comment } : {}),
        }),
      successMessage: '已通过',
      offline: () => undefined,
      offlineMessage: '后端审批接口尚未就绪，本次操作仅在本地演示',
    });
    if (result.ok) {
      setApproveTarget(null);
      list.reload();
    }
  };

  const doReject = async (task: FlowTask, reason: string, toPrev: boolean): Promise<void> => {
    const result = await performAction({
      action: () =>
        flowApi.handle(task.id, {
          action: toPrev ? FlowAction.REJECT_TO_PREV : FlowAction.REJECT_TO_HANDLER,
          comment: reason,
        }),
      successMessage: toPrev ? '已退回上一节点' : '已退回经办人',
      offline: () => undefined,
      offlineMessage: '后端审批接口尚未就绪，本次操作仅在本地演示',
    });
    if (result.ok) {
      setRejectTarget(null);
      setRejectToPrev(null);
      list.reload();
    }
  };

  const doTerminate = async (task: FlowTask, reason: string): Promise<void> => {
    const result = await performAction({
      action: () => flowApi.handle(task.id, { action: FlowAction.TERMINATE, comment: reason }),
      successMessage: '已终止该流程',
      offline: () => undefined,
      offlineMessage: '后端审批接口尚未就绪，本次操作仅在本地演示',
    });
    if (result.ok) {
      setTerminateTarget(null);
      list.reload();
    }
  };

  if (!canEnter) {
    return (
      <div className="ilc-page">
        <PageHeader title="待办与审批" />
        <Alert
          type="warning"
          showIcon
          message="你没有查看待办的权限"
          description="需要发票、合同或费用的查看权限之一。请联系租户管理员在「角色权限」里分配。"
        />
      </div>
    );
  }

  return (
    <div className="ilc-page">
      <PageHeader
        title={props.title}
        description={props.description}
        extra={<Button onClick={list.reload}>刷新</Button>}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="「待我处理」按角色匹配，不是按具体人"
        description={
          <span>
            同一岗位常有多人轮值，任务上记录的是<strong>处理角色</strong>——
            只要你拥有该角色就会看到。合同与报销还要过一道<strong>数据范围</strong>：
            部门负责人只审得到本部门的单子。
            <br />
            看不到某类业务的待办，说明你的角色没有那类业务的查看权限——不是任务不存在。
          </span>
        }
      />

      <DataTable<FlowTask>
        storageKey="approval-center"
        exportFileName="待办与审批"
        exportColumns={[
          { title: '业务类型', value: (r) => BIZ_TYPE_LABELS[r.bizType] ?? r.bizType },
          { title: '单据', value: (r) => r.bizSummary?.title ?? r.bizId },
          { title: '对方/申请人', value: (r) => r.bizSummary?.subtitle ?? '' },
          { title: '当前节点', dataIndex: 'nodeName' },
          { title: '处理角色', dataIndex: 'assigneeRoleName' },
          { title: '应处理时间', value: (r) => (r.dueAt ? dayjs(r.dueAt).format('YYYY-MM-DD HH:mm') : '') },
          { title: '是否超时', value: (r) => (r.overdue ? '是' : '否') },
        ]}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        scrollX={1400}
        emptyText="当前没有待你处理的待办"
        toolbar={
          <Space wrap>
            {/* 锁定业务类型时不显示切换——那个工作台就该只看一类 */}
            {!props.lockedBizType && (
              <Select
                allowClear
                placeholder="业务类型"
                style={{ width: 150 }}
                value={list.query.bizType}
                onChange={(v) => list.setQuery({ bizType: v })}
                options={Object.entries(BIZ_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
              />
            )}
            <Button
              type={list.query.mine === true ? 'primary' : 'default'}
              onClick={() => list.setQuery({ mine: list.query.mine === true ? undefined : true })}
            >
              只看我的
            </Button>
            <Button
              type={list.query.status === undefined ? 'primary' : 'default'}
              onClick={() => list.setQuery({ status: undefined })}
            >
              含已处理
            </Button>
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Card size="small" title="审批动作的语义" style={{ marginTop: 16 }}>
        <Descriptions size="small" column={{ xs: 1, md: 2 }}>
          <Descriptions.Item label="通过">
            推进到下一节点。金额超过你角色的审核上限时会被后端拦下（422）——该由更高权限的人批。
          </Descriptions.Item>
          <Descriptions.Item label="退回经办人">
            <strong>不终止流程</strong>。实例置为「已退回」并记住从哪个节点恢复；
            经办人改完重新提交后从那里继续，多次退回都留在同一个实例里。
          </Descriptions.Item>
          <Descriptions.Item label="退回上一节点">
            回到最近的一个审批节点，由前一位审批人重新判断。
          </Descriptions.Item>
          <Descriptions.Item label="终止">
            流程结束。报销单被终止时会<strong>自动释放名下的发票</strong>，
            否则那些票会被永久占住谁也报不了。
          </Descriptions.Item>
          <Descriptions.Item label="特批（特例放行）">
            高风险单据的专项放行通道，必须填放行依据。依据记在<strong>流程变量</strong>里，
            随流程生命周期失效——不是发票字段，避免"放行一次永久放行"。
          </Descriptions.Item>
          <Descriptions.Item label="超时">
            节点超时后任务被标记为 TIMEOUT 并通知处理角色。
            <strong>目前不会自动升级或改派</strong>——需要人工重新指派，这一条记在技术文档的已知缺口里。
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <ConfirmDanger
        open={approveTarget !== null}
        title={`通过：${approveTarget?.bizSummary?.title ?? ''}`}
        description={
          <span>
            {approveTarget?.nodeName}
            {approveTarget?.nodeKey?.includes('release') && (
              <>
                <br />
                <br />
                <strong>这是特例放行节点</strong>：高风险单据走专项审批。放行依据必填并留痕，
                写清"为什么这笔可以放"。
              </>
            )}
            <br />
            <br />
            金额 {approveTarget?.bizSummary?.amountCents !== null && approveTarget?.bizSummary?.amountCents !== undefined ? <Money cents={approveTarget.bizSummary.amountCents} /> : '（未知）'}
          </span>
        }
        level="normal"
        reasonLabel={
          approveTarget?.nodeKey?.includes('release')
            ? '放行依据（必填，将写入审批留痕）'
            : '审批意见（选填）'
        }
        minReasonLength={approveTarget?.nodeKey?.includes('release') ? 8 : 0}
        confirmText="确认通过"
        onConfirm={(comment) => (approveTarget ? void doApprove(approveTarget, comment) : undefined)}
        onCancel={() => setApproveTarget(null)}
      />

      <ConfirmDanger
        open={rejectTarget !== null}
        title={`退回经办人：${rejectTarget?.bizSummary?.title ?? ''}`}
        description="退回后流程不终止，经办人修改并重新提交后从当前节点继续。退回原因会通知到经办人。"
        level="high"
        reasonLabel="退回原因（必填，将通知经办人）"
        minReasonLength={4}
        reasonPresets={['金额与发票不一致', '缺少必要附件', '事由说明不充分', '条款需与对方重新确认']}
        confirmText="确认退回"
        onConfirm={(reason) => (rejectTarget ? void doReject(rejectTarget, reason, false) : undefined)}
        onCancel={() => setRejectTarget(null)}
      />

      <ConfirmDanger
        open={rejectToPrev !== null}
        title={`退回上一节点：${rejectToPrev?.bizSummary?.title ?? ''}`}
        description="回到最近的一个审批节点，由前一位审批人重新判断。适用于「前面那段判断的依据我不认同」。"
        level="high"
        reasonLabel="退回原因（必填）"
        minReasonLength={4}
        confirmText="确认退回"
        onConfirm={(reason) => (rejectToPrev ? void doReject(rejectToPrev, reason, true) : undefined)}
        onCancel={() => setRejectToPrev(null)}
      />

      <ConfirmDanger
        open={terminateTarget !== null}
        title={`终止流程：${terminateTarget?.bizSummary?.title ?? ''}`}
        description="流程就此结束。报销单被终止时会自动释放名下的发票（它们可以重新用于别的报销单）。这一步不可逆。"
        level="high"
        reasonLabel="终止原因（必填，将写入操作日志）"
        minReasonLength={8}
        acknowledgeText="我确认终止该流程且不可恢复"
        confirmText="确认终止"
        onConfirm={(reason) => (terminateTarget ? void doTerminate(terminateTarget, reason) : undefined)}
        onCancel={() => setTerminateTarget(null)}
      />
    </div>
  );
}

export default ApprovalBoard;
